import React, { useState, useRef, useEffect } from 'react';
import { cn } from './utils';

interface Option {
  value: string;
  label: string;
}

interface ComboboxProps {
  value: string;
  onValueChange: (value: string) => void;
  options: Option[];
  inputValue: string;
  onInputChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export const Combobox: React.FC<ComboboxProps> = ({ value, onValueChange, options, inputValue, onInputChange, placeholder, className }) => {
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState<number>(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (open && highlighted >= 0 && listRef.current) {
      const el = listRef.current.children[highlighted] as HTMLElement;
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
  }, [highlighted, open]);

  useEffect(() => {
    if (!open) setHighlighted(-1);
  }, [open, inputValue]);

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    onInputChange(e.target.value);
    setOpen(true);
  };

  const handleSelect = (option: Option) => {
    onValueChange(option.value);
    onInputChange(option.label);
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setOpen(true);
      setHighlighted(0);
      return;
    }
    if (e.key === 'ArrowDown') {
      setHighlighted(h => Math.min(h + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      setHighlighted(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter' && highlighted >= 0 && open) {
      handleSelect(options[highlighted]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!inputRef.current?.contains(e.target as Node) && !listRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  return (
    <div className={cn("relative w-full", className)}>
      <input
        ref={inputRef}
        className="w-full px-3 py-2 border border-white/10 rounded-md bg-white/5 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition text-sm"
        value={inputValue}
        onChange={handleInput}
        onFocus={() => {
          onInputChange('');
          setOpen(true);
        }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoComplete="off"
        aria-autocomplete="list"
        aria-controls="combobox-listbox"
        aria-expanded={open}
        aria-activedescendant={highlighted >= 0 ? `combobox-option-${highlighted}` : undefined}
      />
      {open && options.length > 0 && (
        <ul
          ref={listRef}
          id="combobox-listbox"
          className="absolute z-20 mt-1 w-full bg-popover border border-border rounded-md shadow-lg max-h-56 overflow-auto py-1"
          role="listbox"
        >
          {options.map((option, idx) => (
            <li
              key={option.value}
              id={`combobox-option-${idx}`}
              role="option"
              aria-selected={value === option.value}
              className={cn(
                "px-3 py-2 cursor-pointer select-none text-sm transition-colors",
                idx === highlighted ? 'bg-accent text-accent-foreground' : 'text-popover-foreground',
                value === option.value ? 'font-medium' : ''
              )}
              onMouseDown={e => e.preventDefault()}
              onClick={() => handleSelect(option)}
              onMouseEnter={() => setHighlighted(idx)}
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}; 